import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  INVOICE_KIND_LABELS,
  type InvoiceResponse,
  type InvoicesResponse,
  italianToday,
  type ManualInvoiceKind,
  type ManualInvoiceRequest,
  manualTotals,
  parseDecimal,
  sameDocument,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { ClientPicker } from '../quotes/ClientPicker.js';
import { treasuryKeys } from './treasury-ui.js';

interface Due {
  dueDate: string;
  amount: string;
}

interface Draft {
  kind: ManualInvoiceKind;
  subjectId: string | null;
  number: string;
  date: string;
  taxable: string;
  vat: string;
  withholding: string;
  paymentTerms: string;
  notes: string;
  dues: Due[];
}

const money = (value: string) => {
  const number = parseDecimal(value);
  return number === null ? null : number.toFixed(2);
};

/**
 * Fatture passive › Nuovo documento (1.59): an invoice that never reaches
 * Gestify (rare: a private person) or a pro forma, a debt like the others.
 * The same page corrects one already entered by hand.
 */
export function ManualInvoicePage() {
  const { id } = useParams();
  const editing = Boolean(id);
  usePageTitle(editing ? 'Correggi il documento' : 'Nuovo documento');
  const existing = useQuery({
    queryKey: treasuryKeys.invoice(id ?? ''),
    queryFn: () => apiRequest<InvoiceResponse>(`/treasury/invoices/${id}`),
    enabled: editing,
  });
  if (editing && existing.isPending) return <p className="muted">Caricamento…</p>;
  if (editing && existing.isError) return <p className="error">{existing.error.message}</p>;
  return <ManualInvoiceForm invoice={existing.data ?? null} />;
}

function ManualInvoiceForm({ invoice }: { invoice: InvoiceResponse | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [draft, setDraft] = useState<Draft>(() =>
    invoice
      ? {
          kind: invoice.kind === 'proforma' ? 'proforma' : 'fattura',
          subjectId: invoice.subjectId,
          number: invoice.number,
          date: invoice.date,
          taxable: invoice.taxable,
          vat: invoice.vat,
          withholding: invoice.withholding,
          paymentTerms: invoice.paymentTerms ?? '',
          notes: invoice.notes ?? '',
          dues: invoice.dues.map((due) => ({ dueDate: due.dueDate, amount: due.amount })),
        }
      : {
          kind: 'proforma',
          // Back from Anagrafica with the supplier just entered.
          subjectId: params.get('cliente'),
          number: '',
          date: italianToday(),
          taxable: '',
          vat: '',
          withholding: '0',
          paymentTerms: '',
          notes: '',
          dues: [],
        },
  );
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const all = useQuery({
    queryKey: treasuryKeys.invoices,
    queryFn: () => apiRequest<InvoicesResponse>('/treasury/invoices'),
  });

  const amounts = {
    taxable: money(draft.taxable),
    vat: money(draft.vat),
    withholding: money(draft.withholding || '0'),
  };
  const complete = amounts.taxable !== null && amounts.vat !== null && amounts.withholding !== null;
  const totals = complete
    ? manualTotals({
        taxable: amounts.taxable!,
        vat: amounts.vat!,
        withholding: amounts.withholding!,
      })
    : null;
  const duesSum = draft.dues.reduce((sum, due) => sum + toCents(money(due.amount) ?? '0'), 0);
  const duesWrong = totals !== null && draft.dues.length > 0 && duesSum !== toCents(totals.netDue);
  const double = (all.data?.invoices ?? []).find(
    (item) =>
      item.id !== invoice?.id &&
      item.direction === 'ricevuta' &&
      sameDocument(item, { subjectId: draft.subjectId, number: draft.number, date: draft.date }),
  );

  const save = useMutation({
    mutationFn: () => {
      const body: ManualInvoiceRequest = {
        kind: draft.kind,
        subjectId: draft.subjectId ?? '',
        number: draft.number,
        date: draft.date,
        taxable: amounts.taxable ?? '',
        vat: amounts.vat ?? '',
        withholding: amounts.withholding ?? '',
        paymentTerms: draft.paymentTerms,
        notes: draft.notes,
        dues: draft.dues.map((due) => ({ dueDate: due.dueDate, amount: money(due.amount) ?? '' })),
      };
      return invoice
        ? apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/manual`, {
            method: 'PUT',
            body,
          })
        : apiRequest<InvoiceResponse>('/treasury/invoices', { method: 'POST', body });
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(treasuryKeys.invoice(saved.id), saved);
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
      navigate(`/tesoreria/fatture/${saved.id}`, { replace: true });
    },
  });

  const ready =
    draft.subjectId !== null && draft.number.trim() !== '' && totals !== null && !duesWrong;
  const back = invoice ? `/tesoreria/fatture/${invoice.id}` : '/tesoreria/fatture-passive';

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to="/tesoreria/fatture-passive">Fatture passive</Link>
          </p>
          <h1>{invoice ? `Correggi ${invoice.number}` : 'Nuovo documento'}</h1>
          <p className="muted">
            Un documento che non arriva da Gestify: una pro-forma, che poi sarà sostituita dalle sue
            fatture, o una fattura che non passa dallo SDI (per esempio di un privato). È un debito
            come gli altri: va nello scadenzario e si paga dalla banca.
          </p>
        </div>
      </div>

      <div className="card stack">
        <div className="form-grid">
          <Field label="Tipo">
            <select
              value={draft.kind}
              onChange={(event) => set('kind', event.target.value as ManualInvoiceKind)}
            >
              <option value="proforma">{INVOICE_KIND_LABELS.proforma}</option>
              <option value="fattura">{INVOICE_KIND_LABELS.fattura}</option>
            </select>
          </Field>
          <Field label="Numero">
            <input
              type="text"
              maxLength={60}
              value={draft.number}
              onChange={(event) => set('number', event.target.value)}
            />
          </Field>
          <Field label="Data">
            <input
              type="date"
              value={draft.date}
              onChange={(event) => set('date', event.target.value)}
            />
          </Field>
        </div>
        <ClientPicker
          label="Fornitore"
          role="fornitore"
          value={draft.subjectId}
          onChange={(subjectId) => set('subjectId', subjectId)}
          returnTo={
            invoice
              ? `/tesoreria/fatture/${invoice.id}/modifica`
              : '/tesoreria/fatture-passive/nuovo'
          }
        />
        {double && (
          <p className="notice small">
            C’è già <Link to={`/tesoreria/fatture/${double.id}`}>{double.number}</Link> dello stesso
            fornitore del {formatDate(double.date)}: controlla di non caricarlo due volte.
          </p>
        )}
        <div className="form-grid">
          <Field label="Imponibile">
            <DecimalInput
              value={draft.taxable}
              onChange={(value) => set('taxable', value)}
              decimals={[2, 2]}
            />
          </Field>
          <Field label="IVA">
            <DecimalInput
              value={draft.vat}
              onChange={(value) => set('vat', value)}
              decimals={[2, 2]}
            />
          </Field>
          <Field
            label="Ritenuta d’acconto"
            hint="La versa K-Rev con l’F24: non si paga al fornitore."
          >
            <DecimalInput
              value={draft.withholding}
              onChange={(value) => set('withholding', value)}
              decimals={[2, 2]}
            />
          </Field>
        </div>
        <dl className="totals">
          <div>
            <dt>Totale documento</dt>
            <dd>{totals ? formatMoney(totals.total) : '—'}</dd>
          </div>
          <div className="grand">
            <dt>Netto da pagare</dt>
            <dd>{totals ? formatMoney(totals.netDue) : '—'}</dd>
          </div>
        </dl>
      </div>

      <div className="card stack">
        <div className="row between">
          <h2>Scadenze</h2>
          <button
            type="button"
            onClick={() =>
              set('dues', [
                ...draft.dues,
                {
                  dueDate: draft.date,
                  amount: totals ? fromCents(Math.max(toCents(totals.netDue) - duesSum, 0)) : '',
                },
              ])
            }
          >
            Aggiungi una scadenza
          </button>
        </div>
        {draft.dues.length === 0 ? (
          <p className="muted">
            Nessuna scadenza: il netto da pagare scade alla data del documento.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Scadenza</th>
                  <th className="numeric">Importo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draft.dues.map((due, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="date"
                        value={due.dueDate}
                        onChange={(event) =>
                          set(
                            'dues',
                            draft.dues.map((item, position) =>
                              position === index ? { ...item, dueDate: event.target.value } : item,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="numeric">
                      <DecimalInput
                        aria-label={`Importo della scadenza ${index + 1}`}
                        value={due.amount}
                        onChange={(value) =>
                          set(
                            'dues',
                            draft.dues.map((item, position) =>
                              position === index ? { ...item, amount: value } : item,
                            ),
                          )
                        }
                        decimals={[2, 2]}
                      />
                    </td>
                    <td className="numeric">
                      <button
                        type="button"
                        className="link"
                        onClick={() =>
                          set(
                            'dues',
                            draft.dues.filter((_, position) => position !== index),
                          )
                        }
                      >
                        Togli
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {duesWrong && (
          <p className="error">
            Le scadenze sommano {formatMoney(fromCents(duesSum))}, il netto da pagare è{' '}
            {formatMoney(totals!.netDue)}.
          </p>
        )}
      </div>

      <div className="card stack">
        <Field label="Condizioni di pagamento">
          <input
            type="text"
            maxLength={200}
            value={draft.paymentTerms}
            onChange={(event) => set('paymentTerms', event.target.value)}
          />
        </Field>
        <Field label="Note">
          <textarea
            rows={3}
            value={draft.notes}
            onChange={(event) => set('notes', event.target.value)}
          />
        </Field>
        <p className="muted small">
          Il PDF si allega dalla pagina del documento, dopo il salvataggio: va nella cartella del
          fornitore su SharePoint.
        </p>
      </div>

      {save.error && <p className="error">{save.error.message}</p>}
      <div className="actions">
        <Link className="button" to={back}>
          Annulla
        </Link>
        <button
          type="button"
          className="primary"
          disabled={!ready || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Attendere…' : 'Salva'}
        </button>
      </div>
    </section>
  );
}
